import { useState } from 'react'
import useConversationsStore from '@/stores/useConversationsStore'
import { useAwsCredentialsManager } from '../context/AWS-credentials.context'
import { getConfig } from '@/config/runtime'

interface UseRenameConversationProps {
  conversationId: string
  initialName: string
}

interface UseRenameConversationReturn {
  isEditing: boolean
  isLoading: boolean
  error: Error | null
  editedName: string
  setEditedName: (name: string) => void
  startEditing: () => void
  cancelEditing: () => void
  submitRename: () => Promise<void>
}

const useRenameConversation = ({
  conversationId,
  initialName,
}: UseRenameConversationProps): UseRenameConversationReturn => {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [editedName, setEditedName] = useState(initialName)

  const { applicationId, credentials } = useAwsCredentialsManager()
  const updateConversationName = useConversationsStore((state) => state.updateConversationName)

  const startEditing = () => {
    setEditedName(initialName)
    setIsEditing(true)
    setError(null)
  }

  const cancelEditing = () => {
    setEditedName(initialName)
    setIsEditing(false)
    setError(null)
  }

  const submitRename = async (): Promise<void> => {
    if (!editedName.trim()) {
      setError(new Error('Conversation name cannot be empty'))
      return
    }

    setIsLoading(true)
    setError(null)

    const API_ENDPOINT = `${getConfig().plus.apiUrl}/rename-conversation`

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          conversationName: editedName.trim(),
          applicationId,
          accessKeyId: credentials?.AccessKeyId,
          secretAccessKey: credentials?.SecretAccessKey,
          sessionToken: credentials?.SessionToken,
        }),
      })

      if (response.ok) {
        const responseData = await response.json()

        if (responseData.success) {
          updateConversationName(conversationId, editedName.trim())
          setIsEditing(false)
        } else {
          throw new Error(responseData.message || 'Failed to rename conversation')
        }
      } else {
        const errorText = await response.text()
        throw new Error(`API error: ${response.status} - ${errorText}`)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Network or other error during rename'
      console.error('Error renaming conversation:', errorMessage)
      setError(new Error(errorMessage))
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isEditing,
    isLoading,
    error,
    editedName,
    setEditedName,
    startEditing,
    cancelEditing,
    submitRename,
  }
}

export default useRenameConversation